import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { useStageStore } from '../stores/stageStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { createStage, updateStage, deleteStage, createDefaultStages } from '../services/api';
import { Input, Button, ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, RowActions, RowAction, btnPrimary, btnSecondary, FormFieldWithError } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { Role } from '@redpanda/types';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

// 回應訊息常量（對應後端翻譯鍵值）
const RESPONSE_MESSAGES = {
  STAGES_ALREADY_EXIST: 'defaultStagesExist',
  STAGES_CREATED_SUCCESS: 'defaultStagesCreated'
} as const;

interface Stage {
  id: string;
  name: string;
  orderIndex: number;
  probability: number;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    opportunities: number;
  };
}

interface StageFormData {
  name: string;
  orderIndex: number;
  probability: number;
}

const OpportunityStageManagePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const userRole = user?.role as Role;

  const {
    stages,
    loading,
    fetchStages,
    addStage,
    updateStage: updateStageInStore,
    deleteStage: deleteStageFromStore,
  } = useStageStore();
  const [isInitializingDefaults, setIsInitializingDefaults] = useState(false);
  
  // 表單狀態
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [formData, setFormData] = useState<StageFormData>({
    name: '',
    orderIndex: 1,
    probability: 0.1,
  });
  const [formLoading, setFormLoading] = useState(false);

  // 刪除確認對話框
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; stageId: string | null }>({
    isOpen: false,
    stageId: null,
  });
  const [isDeletingStage, setIsDeletingStage] = useState(false);

  // 載入階段資料
  useEffect(() => {
    if (isAuthenticated && !isUserLoading) {
      fetchStages();
    }
  }, [isAuthenticated, isUserLoading, fetchStages]);

  // 初始化預設階段
  const handleInitDefaults = async () => {
    setIsInitializingDefaults(true);
    try {
      const response = await createDefaultStages(i18n.language);
      // 根據後端返回的訊息鍵值顯示對應的翻譯
      if (response.data.message === RESPONSE_MESSAGES.STAGES_ALREADY_EXIST) {
        toast.success(t('opportunities.stages.defaultStagesExist', { ns: 'pages' }));
      } else if (response.data.message === RESPONSE_MESSAGES.STAGES_CREATED_SUCCESS) {
        toast.success(t('opportunities.stages.success.defaultStagesCreated', { ns: 'pages' }));
      }
      await fetchStages(); // 重新載入階段列表
      notifyDataChanged('opportunities'); // 階段影響銷售漏斗與階段統計
    } catch (error: any) {
      console.error('初始化預設階段失敗:', error);
      toast.error(t('opportunities.stages.errors.initDefaultsFailed', { ns: 'pages' }));
    } finally {
      setIsInitializingDefaults(false);
    }
  };

  // 開始新增階段
  const handleStartAdd = () => {
    const maxOrderIndex = stages.length > 0 ? Math.max(...stages.map(s => s.orderIndex)) : 0;
    setFormData({
      name: '',
      orderIndex: maxOrderIndex + 1,
      probability: 0.1,
    });
    setEditingStage(null);
    setShowAddForm(true);
  };

  // 開始編輯階段
  const handleStartEdit = (stage: Stage) => {
    setFormData({
      name: stage.name,
      orderIndex: stage.orderIndex,
      probability: stage.probability,
    });
    setEditingStage(stage);
    setShowAddForm(true);
  };

  // 取消編輯
  const handleCancelEdit = () => {
    setShowAddForm(false);
    setEditingStage(null);
    setFormData({ name: '', orderIndex: 1, probability: 0.1 });
  };

  // 提交表單
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(t('opportunities.stages.errors.nameRequired', { ns: 'pages' }));
      return;
    }
    
    if (formData.probability < 0 || formData.probability > 1) {
      toast.error(t('opportunities.stages.errors.probabilityRange', { ns: 'pages' }));
      return;
    }

    setFormLoading(true);
    try {
      if (editingStage) {
        // 更新現有階段
        const response = await updateStage(editingStage.id, {
          name: formData.name.trim(),
          orderIndex: formData.orderIndex,
          probability: formData.probability,
        });
        
        // 更新本地存储（原本誤呼叫 API 的 updateStage，會再送一次 PATCH）
        updateStageInStore(editingStage.id, response.data);
        toast.success(t('opportunities.stages.success.stageUpdated', { ns: 'pages' }));
      } else {
        // 創建新階段
        const response = await createStage({
          name: formData.name.trim(),
          orderIndex: formData.orderIndex,
          probability: formData.probability,
        });
        
        // 添加到本地存储
        addStage(response.data);
        toast.success(t('opportunities.stages.success.stageCreated', { ns: 'pages' }));
      }
      
      handleCancelEdit(); // 關閉表單
    } catch (error: any) {
      console.error('操作失敗:', error);
      const errorMessage = error.response?.data?.error || t('opportunities.stages.errors.operationFailed', { ns: 'pages' });
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  // 刪除階段
  const handleDelete = (stageId: string) => {
    setDeleteModal({ isOpen: true, stageId });
  };

  const confirmDelete = async () => {
    if (!deleteModal.stageId) return;
    
    setIsDeletingStage(true);
    try {
      await deleteStage(deleteModal.stageId);
      
      // 從本地存储中移除
      deleteStageFromStore(deleteModal.stageId);
      toast.success(t('opportunities.stages.success.stageDeleted', { ns: 'pages' }));
    } catch (error: any) {
      console.error('刪除失敗:', error);
      const errorMessage = error.response?.data?.error || t('opportunities.stages.errors.deleteFailed', { ns: 'pages' });
      toast.error(errorMessage);
    } finally {
      setIsDeletingStage(false);
      setDeleteModal({ isOpen: false, stageId: null });
    }
  };

  // 移動階段順序
  const moveStage = async (stageId: string, direction: 'up' | 'down') => {
    const currentStage = stages.find(s => s.id === stageId);
    if (!currentStage) return;

    const sortedStages = [...stages].sort((a, b) => a.orderIndex - b.orderIndex);
    const currentIndex = sortedStages.findIndex(s => s.id === stageId);
    
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === sortedStages.length - 1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const targetStage = sortedStages[targetIndex];

    try {
      // 交換順序索引
      await Promise.all([
        updateStage(currentStage.id, { orderIndex: targetStage.orderIndex }),
        updateStage(targetStage.id, { orderIndex: currentStage.orderIndex }),
      ]);
      
      // 更新本地存储
      updateStageInStore(currentStage.id, { orderIndex: targetStage.orderIndex });
      updateStageInStore(targetStage.id, { orderIndex: currentStage.orderIndex });
      
      toast.success(t('opportunities.stages.success.orderAdjusted', { ns: 'pages' }));
    } catch (error: any) {
      console.error('調整順序失敗:', error);
      toast.error(t('opportunities.stages.errors.adjustOrderFailed', { ns: 'pages' }));
    }
  };

  // 權限檢查
  if (!isAuthenticated || isUserLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (userRole !== Role.SUPER_ADMIN && userRole !== Role.ADMIN) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('opportunities.stages.permissions.insufficient', { ns: 'pages' })}</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('opportunities.stages.permissions.adminOnly', { ns: 'pages' })}
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      {/* 麵包屑取代「返回列表」按鈕；頁首一主一次要（§3-5） */}
      <nav className="mb-1 text-[13px] text-fg-secondary">
        <Link to="/opportunities" className="hover:text-fg transition-colors">{t('opportunities.list.title', { ns: 'pages' })}</Link>
        <span className="mx-1.5 text-fg-muted">/</span>
      </nav>
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('opportunities.stages.pageTitle', { ns: 'pages' })}</h1>
          <div className="flex flex-wrap justify-end gap-3">
            <button
              onClick={handleInitDefaults}
              disabled={isInitializingDefaults || loading}
              className={btnSecondary}
            >
              {isInitializingDefaults ? t('opportunities.stages.initializing', { ns: 'pages' }) : t('opportunities.stages.initDefaults', { ns: 'pages' })}
            </button>
            <button onClick={handleStartAdd} disabled={loading} className={btnPrimary}>
              {t('opportunities.stages.add', { ns: 'pages' })}
            </button>
          </div>
        </div>
      </div>

      {/* 新增/編輯表單 */}
      {showAddForm && (
        <div className="mb-6 bg-surface rounded-lg border border-line p-6">
          <h2 className="text-base font-semibold text-fg mb-4">
            {editingStage ? t('opportunities.stages.edit', { ns: 'pages' }) : t('opportunities.stages.add', { ns: 'pages' })}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormFieldWithError label={t('opportunities.stages.name', { ns: 'pages' })} required htmlFor="name">
                <Input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </FormFieldWithError>
              <FormFieldWithError label={t('opportunities.stages.orderIndex', { ns: 'pages' })} required htmlFor="orderIndex">
                <Input
                  type="number"
                  id="orderIndex"
                  value={formData.orderIndex}
                  onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 1 })}
                  min="1"
                  required
                />
              </FormFieldWithError>
              <FormFieldWithError
                label={`${t('opportunities.stages.probability', { ns: 'pages' })} (0-1)`}
                required
                htmlFor="probability"
                helpText={t('opportunities.stages.probabilityHint', { ns: 'pages' })}
              >
                <Input
                  type="number"
                  id="probability"
                  value={formData.probability}
                  onChange={(e) => setFormData({ ...formData, probability: parseFloat(e.target.value) || 0 })}
                  min="0"
                  max="1"
                  step="0.01"
                  required
                />
              </FormFieldWithError>
            </div>
            <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-line">
              <Button type="button" variant="secondary" onClick={handleCancelEdit} disabled={formLoading}>
                {t('opportunities.stages.cancel', { ns: 'pages' })}
              </Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? t('opportunities.stages.processing', { ns: 'pages' }) : editingStage ? t('opportunities.stages.update', { ns: 'pages' }) : t('opportunities.stages.add', { ns: 'pages' })}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* 階段列表：統一用 @redpanda/ui Table */}
      {loading ? (
        <div className="bg-surface rounded-lg border border-line py-8 text-center text-sm text-fg-secondary">
          {t('opportunities.stages.loading', { ns: 'pages' })}
        </div>
      ) : stages.length === 0 ? (
        <div className="bg-surface rounded-lg border border-line py-8 text-center">
          <p className="text-sm text-fg-secondary mb-3">{t('opportunities.stages.noStages', { ns: 'pages' })}</p>
          <button onClick={handleInitDefaults} disabled={isInitializingDefaults} className="text-sm text-accent hover:underline">
            {t('opportunities.stages.clickToInitDefaults', { ns: 'pages' })}
          </button>
        </div>
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
            <Table style={{ tableLayout: 'fixed', minWidth: '760px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">{t('opportunities.stages.order', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-48">{t('opportunities.stages.name', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-28">{t('opportunities.stages.probability', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-28">{t('opportunities.stages.opportunityCount', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('opportunities.stages.createdAt', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-32 pl-10">{t('opportunities.stages.actions', { ns: 'pages' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stages.map((stage, index) => {
                  const inUse = Boolean(stage._count?.opportunities && stage._count.opportunities > 0);
                  return (
                    <TableRow key={stage.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="w-5 tabular-nums">{stage.orderIndex}</span>
                          <button
                            onClick={() => moveStage(stage.id, 'up')}
                            disabled={index === 0}
                            aria-label={t('opportunities.stages.moveUp', { ns: 'pages' })}
                            className="p-1 text-fg-muted hover:text-fg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowUpIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => moveStage(stage.id, 'down')}
                            disabled={index === stages.length - 1}
                            aria-label={t('opportunities.stages.moveDown', { ns: 'pages' })}
                            className="p-1 text-fg-muted hover:text-fg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowDownIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium truncate">{stage.name}</TableCell>
                      <TableCell numeric>{Math.round(stage.probability * 100)}%</TableCell>
                      <TableCell numeric>{stage._count?.opportunities || 0}</TableCell>
                      <TableCell numeric className="!text-fg-secondary">{formatTableDate(stage.createdAt, i18n.language)}</TableCell>
                      <TableCell className="pl-10">
                        <RowActions>
                          <RowAction onClick={() => handleStartEdit(stage)}>{t('common.actions.edit')}</RowAction>
                          <RowAction
                            tone="danger"
                            onClick={() => handleDelete(stage.id)}
                            disabled={inUse}
                            title={inUse ? t('opportunities.stages.deleteTooltip', { ns: 'pages' }) : t('opportunities.stages.deleteStage', { ns: 'pages' })}
                          >
                            {t('common.actions.delete')}
                          </RowAction>
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* 刪除確認對話框 */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => {
          if (!isDeletingStage) {
            setDeleteModal({ isOpen: false, stageId: null });
          }
        }}
        onConfirm={confirmDelete}
        title={t('opportunities.stages.confirmDelete', { ns: 'pages' })}
        message={t('opportunities.stages.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('opportunities.stages.cancel', { ns: 'pages' })}
        type="danger"
        isLoading={isDeletingStage}
        loadingText={t('common.status.deleting')}
        t={t}
      />
    </div>
  );
};

export default OpportunityStageManagePage;